import React from 'react';
import classNames from 'classnames';
import {graphql, useLazyLoadQuery} from 'react-relay/hooks';
import {FormattedMessage} from 'react-intl'
import {CartStateContext, CartDispatchContext} from '../../../contexts/cart';
import {ErrorBoundary} from 'react-error-boundary';
import SuspenseList from '../../../components/SuspenseList';
import Skeleton from '../../../components/Skeleton';
import Footer from './footer';
import OrderItem from './order-item';
import PopUp from '../../../components/pop-up/pop-up.js';
import Button from '../../../components/button/button';
import './order.scss';
import ErrorFallback from '../../../components/ErrorFallback';
import useSwipe from '../../../use-swipe';
import getAvailableEndpoints from '../../../utilities/get-available-endpoints.js';
import sortCartEndpoints from '../../../utilities/sort-cart-endpoints.js';
import {useErrorMessagesDispatch} from '../error-messages-context.js';
import useNow from '../../../useNow.js';
import {isTrue} from '../../../utility.js';
import useQuery from '../../../useQuery.js';

const OrderContainer = ({land, deleteItemAction, state}) => {
    const {more} = useQuery();
    const serviceId = state[land.pickExs[0].index].serviceId;
    const errorDispatch = useErrorMessagesDispatch();
    const now = useNow();
    const localCartState = land.pickExs.map((item, i) => {
        return state[item.index];
    });
    const {serviceById} = useLazyLoadQuery(
        graphql`
            query orderCartPagesQuery($serviceId: String) {
                serviceById(id: $serviceId) {
                    id
                    name
                    nameUk
                    target
                    endpoints {
                        id
                        quantity
                        start
                        end
                        strategy
                        every
                        price
                        tags
                        labels
                    }
                }
            }
        `,
        {serviceId: serviceId}
    );
    const availableEndpoints = React.useMemo(() => {
        return getAvailableEndpoints({endpoints: serviceById.endpoints, now, showAll: isTrue(more)});
    }, [now, serviceById]);
    const {sortedEndpoints, hasInvalidEndpoints} = sortCartEndpoints({localCartState, availableEndpoints});

    React.useEffect(() => {
        if (hasInvalidEndpoints) {
            errorDispatch({
                type: 'add',
                payload: {
                    type: 'lightError',
                    title: <FormattedMessage defaultMessage='There are unavailable tickets in the order'/>,
                    description: <FormattedMessage defaultMessage='To place an order, remove tickets that are not available for purchase'/>,
                    showDefaultAction: true
                }
            });
        }
    }, []);
    return (
        <>
            {sortedEndpoints.map((item, i) =>
                <OrderItem {...{key: i, entry: item, serviceById, deleteItemAction: deleteItemAction}}/>
            )}
        </>
    )
}

const Order = () => {
    const state = React.useContext(CartStateContext);
    const dispatch = React.useContext(CartDispatchContext);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [popUpVisible, setPopUpVisibility] = React.useState(false);
    const [popUpConfirmAction, setPopUpConfirmAction] = React.useState(null);
    const deleteItemAction = React.useCallback((confirmAction) => {
        setPopUpConfirmAction(confirmAction);
        setPopUpVisibility(true);
    });

    const request = React.useMemo(() => ({
        picks: state.map(({endpointId, serviceId}) => ({
            endpoint: endpointId,
            service: serviceId,
            quantity: 1
        }))
    }), [state]);

    const footerRequest = React.useMemo(() => ({
        picks: state.map(({endpointId, serviceId, quantity, untie}) => ({
            endpoint: endpointId,
            service: serviceId,
            quantity,
            ...(untie && {untie: {
                currency: untie.currency,
                price: untie.price
            }})
        }))
    }), [state]);
    const footerRequestDeferred = React.useDeferredValue(footerRequest);


    const popupConfirm = () => {
        dispatch(popUpConfirmAction);
        setPopUpConfirmAction(null);
        setPopUpVisibility(false);
    }

    const popupCancel = () => {
        setPopUpConfirmAction(null);
        setPopUpVisibility(false);
    }

    React.useEffect(() => {
        if (!data.landImpulse.lands[activeIndex]) {
            setActiveIndex(data.landImpulse.lands.length - 1);
        }
    }, [request]);

    const data = useLazyLoadQuery(
        graphql`
            query orderCartPagesDeferredQuery($request: LandRequestInput) {
                landImpulse(request: $request) {
                    lands {
                        pickExs {
                            index
                        }
                    }
                }
            }
        `,
        {request}
    );

    const leftSwipeAction = () => {
        const newIndex = activeIndex + 1 === state.length ? activeIndex : activeIndex + 1;
        setActiveIndex(newIndex);
    }

    const rightSwipeAction= () => {
        const newIndex = activeIndex - 1 < 0 ? activeIndex : activeIndex - 1;
        setActiveIndex(newIndex);
    };
    const swipeContainer = React.useRef();

    const swipe = useSwipe({ref: swipeContainer, showNext: leftSwipeAction, showPrevious: rightSwipeAction});

    return (
        <div ref={swipeContainer} className={classNames('order flex-1 display-flex position-relative', {'order--several': state.length > 1})}>
            {data.landImpulse.lands.length > 1 && 
                <div className='order__list-nav'>
                    {data.landImpulse.lands.map((x, i) =>
                        <div key={i} className='order__list-nav-item' onClick={() => setActiveIndex(i)}>
                            <div className={classNames('order__list-nav-bar', {'background-color-primary-600': i === activeIndex, 'background-color-primary-100': i !== activeIndex})}></div>
                        </div>
                    )}
                </div>
            }
            <div className='order__list display-flex flex-direction-column-row flex-1'>
                <SuspenseList revealOrder='forwards'>
                    {data.landImpulse.lands.map((land, index) =>
                        <SuspenseList key={index}>
                            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                <React.Suspense fallback={
                                    <div className={classNames('flex-1', {'display-none': activeIndex !== index})}>
                                        <Skeleton/>
                                    </div>
                                }>
                                    <div className={classNames('padding-bottom-2rem flex-direction-column order__items-container', {'display-none': activeIndex !== index, 'display-flex' : activeIndex === index})}>
                                        {activeIndex === index && <OrderContainer {...{land, deleteItemAction, state}}/>}
                                    </div>
                                    <React.Suspense fallback={
                                        <div className={classNames('flex-1', {'display-none': activeIndex !== index})}>
                                            <Skeleton/>
                                        </div>
                                    }>
                                        <div className={classNames('footer-container', {'display-none': activeIndex !== index})}>
                                            <Footer {...{index, request: footerRequestDeferred, pendingRequest: footerRequest !== footerRequestDeferred}}/>
                                        </div>
                                    </React.Suspense>
                                </React.Suspense>
                            </ErrorBoundary>
                        </SuspenseList>
                    )}
                </SuspenseList>
            </div>
            <PopUp {...{mode: 'default', isVisible: popUpVisible, icon: 'alert', title: <FormattedMessage defaultMessage='Remove the ticket from the basket'/>, message: <FormattedMessage defaultMessage='Are you sure you want to remove this ticket from your cart?'/>}}>
                <div className='flex-1'>
                    <Button {...{size: 'lg', color: 'error', fluid: 'always', clickHandler: popupConfirm}}>
                        <FormattedMessage defaultMessage='Remove'/>
                    </Button>
                </div>
                <div className='flex-1'>
                    <Button {...{color: 'secondary-gray', size: 'lg', fluid: 'always', clickHandler: popupCancel}}>
                        <FormattedMessage defaultMessage='Return'/>
                    </Button>
                </div>
            </PopUp>
        </div>
    )
};

export default React.memo(Order);